import { createServerFn } from "@tanstack/react-start";
import { isGoldRelevant } from "@/lib/calendar-keywords";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { normalizeTitle } from "@/lib/calendar-title";

export type CalendarEvent = {
  id: string;
  title: string;
  country: string;
  /** ISO string (UTC) */
  date: string;
  importance: 3;
  forecast: string | null;
  previous: string | null;
  actual: string | null;
  /** CPI releases only: headline and core readings published together. */
  breakdown?: { label: string; previous: string | null }[];
};

/**
 * Pinned official US release schedule (BLS 2026 schedule, Federal Reserve
 * FOMC calendar). Used only to fill the forward window when the weekly
 * provider feed has no remaining high impact events, so the next market
 * moving release is always visible. Times are UTC (08:30 ET converted with DST).
 */
type ScheduledRelease = { title: string; date: string };

export const OFFICIAL_RELEASES_2026: ScheduledRelease[] = [
  { title: "CPI m/m", date: "2026-10-14T12:30:00.000Z" },
  { title: "PPI m/m", date: "2026-10-15T12:30:00.000Z" },
  { title: "Federal Funds Rate", date: "2026-10-28T18:00:00.000Z" },
  { title: "Non-Farm Employment Change", date: "2026-11-06T13:30:00.000Z" },
  { title: "CPI m/m", date: "2026-11-10T13:30:00.000Z" },
  { title: "PPI m/m", date: "2026-11-13T13:30:00.000Z" },
  { title: "Non-Farm Employment Change", date: "2026-12-04T13:30:00.000Z" },
  { title: "Federal Funds Rate", date: "2026-12-09T19:00:00.000Z" },
];

type CacheEntry = { events: CalendarEvent[]; fetchedAt: number };

const CACHE_TTL_MS = 15 * 60 * 1000;
let cache: CacheEntry | null = null;
let inflight: Promise<CacheEntry> | null = null;

async function readDbCache(): Promise<CacheEntry | null> {
  try {
    const { data, error } = await supabaseAdmin
      .from("economic_calendar_cache")
      .select("events, fetched_at")
      .eq("id", 1)
      .maybeSingle();
    if (error || !data) return null;
    const fetchedAt = Date.parse(data.fetched_at as string);
    if (!Number.isFinite(fetchedAt)) return null;
    return { events: (data.events as CalendarEvent[]) ?? [], fetchedAt };
  } catch (err) {
    console.error("[economic-calendar] readDbCache failed:", err);
    return null;
  }
}

async function writeDbCache(entry: CacheEntry): Promise<void> {
  try {
    const { error } = await supabaseAdmin
      .from("economic_calendar_cache")
      .upsert(
        {
          id: 1,
          events: entry.events as unknown as never,
          fetched_at: new Date(entry.fetchedAt).toISOString(),
        },
        { onConflict: "id" },
      );
    if (error) console.error("[economic-calendar] writeDbCache error:", error);
  } catch (err) {
    console.error("[economic-calendar] writeDbCache failed:", err);
  }
}

type FFRaw = {
  title?: string;
  country?: string;
  date?: string;
  impact?: string;
  forecast?: string;
  previous?: string;
  actual?: string;
};

function normalize(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

function minuteIso(value: string | number | Date): string | null {
  const t = value instanceof Date ? value.getTime() : Date.parse(String(value));
  if (!Number.isFinite(t)) return null;
  return new Date(Math.floor(t / 60_000) * 60_000).toISOString();
}

async function fetchFromUpstream(): Promise<CacheEntry> {
  // ForexFactory weekly JSON mirror — free, no auth, ~25 KB.
  // Retry with backoff on 429 rate-limit responses before giving up.
  const MAX_ATTEMPTS = 3;
  let res: Response | null = null;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    res = await fetch(
      "https://nfs.faireconomy.media/ff_calendar_thisweek.json",
      { headers: { Accept: "application/json" } },
    );
    if (res.status !== 429) break;
    if (attempt < MAX_ATTEMPTS - 1) {
      await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
    }
  }

  if (!res || !res.ok) {
    throw new Error(`Calendar request failed: ${res?.status ?? "no response"}`);
  }

  const raw = (await res.json()) as FFRaw[];
  if (!Array.isArray(raw)) {
    throw new Error("Unexpected calendar response shape");
  }

  const cutoff = Date.now() - 60 * 60 * 1000; // keep events from up to 1h ago

  const events: CalendarEvent[] = raw
    .filter(
      (e) =>
        e.country === "USD" &&
        e.impact === "High" &&
        typeof e.title === "string" &&
        typeof e.date === "string" &&
        isGoldRelevant(e.title),
    )
    .map<CalendarEvent>((e, i) => {
      // FF date format: "2026-04-30T13:30:00-04:00" — already ISO with TZ.
      const ts = Date.parse(e.date!);
      const iso = Number.isFinite(ts) ? new Date(ts).toISOString() : e.date!;
      return {
        id: `${e.date}-${i}`,
        date: iso,
        title: e.title!.trim(),
        country: e.country!,
        importance: 3 as const,
        forecast: normalize(e.forecast),
        previous: normalize(e.previous),
        actual: normalize(e.actual),
      };
    })
    .filter((e) => {
      const t = Date.parse(e.date);
      return Number.isFinite(t) && t > cutoff;
    })
    .sort((a, b) => Date.parse(a.date) - Date.parse(b.date))
    .slice(0, 12);

  return { events, fetchedAt: Date.now() };
}

async function mergeActuals(entry: CacheEntry): Promise<CacheEntry> {
  if (entry.events.length === 0) return entry;
  try {
    const dates = entry.events.map((e) => minuteIso(e.date)).filter((v): v is string => Boolean(v));
    const { data, error } = await supabaseAdmin
      .from("economic_calendar_actuals")
      .select("event_date, title, actual")
      .in("event_date", dates);
    if (error || !data || data.length === 0) return entry;

    const map = new Map<string, string>();
    for (const r of data) {
      if (!r.actual) continue;
      const iso = minuteIso(r.event_date as string);
      if (!iso) continue;
      map.set(`${iso}|${normalizeTitle(r.title)}`, r.actual);
    }
    if (map.size === 0) return entry;

    const merged = entry.events.map((e) => {
      if (e.actual) return e;
      const iso = minuteIso(e.date);
      if (!iso) return e;
      const key = `${iso}|${normalizeTitle(e.title)}`;
      const actual = map.get(key);
      return actual ? { ...e, actual } : e;
    });
    return { events: merged, fetchedAt: entry.fetchedAt };
  } catch (err) {
    console.error("mergeActuals failed:", err);
    return entry;
  }
}

/**
 * Drop events whose release is more than 15 minutes in the past, then top up
 * from the pinned official schedule so the nearest upcoming high impact
 * release is always shown, even when it falls in a later week.
 */
function finalize(entry: CacheEntry, keepPastMs = 15 * 60 * 1000): CacheEntry {
  const floor = Date.now() - keepPastMs;
  const live = entry.events.filter((e) => {
    const t = Date.parse(e.date);
    return Number.isFinite(t) && t > floor;
  });
  const seen = new Set(live.map((e) => `${minuteIso(e.date)}|${normalizeTitle(e.title)}`));
  const pinned: CalendarEvent[] = OFFICIAL_RELEASES_2026.filter((r) => {
    const t = Date.parse(r.date);
    return t > floor && !seen.has(`${minuteIso(r.date)}|${normalizeTitle(r.title)}`);
  }).map((r) => ({
    id: `official-${r.date}-${normalizeTitle(r.title)}`,
    title: r.title,
    country: "USD",
    date: r.date,
    importance: 3 as const,
    forecast: null,
    previous: null,
    actual: null,
  }));
  const events = [...live, ...pinned]
    .sort((a, b) => Date.parse(a.date) - Date.parse(b.date))
    .slice(0, 12);
  return { events, fetchedAt: entry.fetchedAt };
}

// Official schedule rows carry no figures. The previous reading is a value
// already published by BLS, so fill it from the official series (6h cache).
const prevCache = new Map<string, { at: number; value: string | null }>();
const PREV_TTL_MS = 6 * 60 * 60 * 1000;
// Last official FOMC target upper bound, used only if FRED is unreachable.
const FED_FUNDS_LAST_OFFICIAL = "4.00%";

async function fillOfficialPrevious(entry: CacheEntry): Promise<CacheEntry> {
  const targets = entry.events.filter((e) => e.id.startsWith("official-") && !e.previous);
  if (targets.length === 0) return entry;
  const { fetchBlsActual } = await import("@/lib/bls-actuals.server");
  const { lookupFredRelease, fetchFredActualForRelease } = await import(
    "@/lib/fred-actuals.server"
  );
  const lookup = async (title: string) => {
    const key = normalizeTitle(title);
    const hit = prevCache.get(key);
    if (hit && Date.now() - hit.at < PREV_TTL_MS) return hit.value;
    let value: string | null = null
    if (lookupFredRelease(title)) {
      // Federal Reserve decision: the previous reading is the current
      // target upper bound published by FRED. Last known official value
      // is the fallback when the key or feed is unavailable.
      value =
        (await fetchFredActualForRelease(title, new Date(Date.now() + 365 * 864e5)).catch(
          () => null,
        )) ?? FED_FUNDS_LAST_OFFICIAL
    } else {
      value = await fetchBlsActual(title).catch(() => null)
    }
    if (value) prevCache.set(key, { at: Date.now(), value });
    return value;
  };
  const titles = [...new Set(targets.map((e) => e.title))];
  const values = new Map<string, string | null>();
  await Promise.all(titles.map(async (t) => values.set(t, await lookup(t))));
  return {
    ...entry,
    events: entry.events.map((e) =>
      e.id.startsWith("official-") && !e.previous
        ? { ...e, previous: values.get(e.title) ?? null }
        : e,
    ),
  };
}

// CPI headline and core print in the same BLS report, so the CPI m/m row
// carries all three reference readings. Weekly feed siblings win over BLS.
const CPI_PARTS = [
  { label: "Core m/m", title: "Core CPI m/m" },
  { label: "CPI m/m", title: "CPI m/m" },
  { label: "CPI y/y", title: "CPI y/y" },
];
async function attachCpiBreakdown(entry: CacheEntry): Promise<CacheEntry> {
  const rows = entry.events.filter((e) => normalizeTitle(e.title) === "cpi m m");
  if (rows.length === 0) return entry;
  const { fetchBlsActual } = await import("@/lib/bls-actuals.server");
  const bls = new Map<string, string | null>();
  await Promise.all(
    CPI_PARTS.map(async (p) => {
      const key = normalizeTitle(p.title);
      const hit = prevCache.get(key);
      if (hit && Date.now() - hit.at < PREV_TTL_MS) return bls.set(p.title, hit.value);
      const v = await fetchBlsActual(p.title).catch(() => null);
      if (v) prevCache.set(key, { at: Date.now(), value: v });
      bls.set(p.title, v);
    }),
  );
  const events = entry.events.map((e) => {
    if (normalizeTitle(e.title) !== "cpi m m") return e;
    const iso = minuteIso(e.date);
    const breakdown = CPI_PARTS.map((p) => {
      const sib = entry.events.find(
        (x) => minuteIso(x.date) === iso && normalizeTitle(x.title) === normalizeTitle(p.title),
      );
      return { label: p.label, previous: sib?.previous ?? bls.get(p.title) ?? null };
    });
    return { ...e, title: "US CPI (Headline & Core)", breakdown };
  });
  return { ...entry, events };
}

const deliver = async (entry: CacheEntry) =>
  attachCpiBreakdown(await mergeActuals(await fillOfficialPrevious(finalize(entry))));

async function loadEntry(): Promise<CacheEntry> {
  const now = Date.now();
  if (cache && now - cache.fetchedAt < CACHE_TTL_MS) return cache;
  const dbEntry = await readDbCache();
  if (dbEntry && now - dbEntry.fetchedAt < CACHE_TTL_MS) {
    cache = dbEntry;
    return dbEntry;
  }
  if (!inflight) {
    inflight = fetchFromUpstream()
      .then(async (entry) => {
        // A fresh fetch is authoritative, even when empty: past events must
        // never be revived from an older cache row.
        cache = entry;
        await writeDbCache(entry);
        return entry;
      })
      .catch((err) => {
        console.error("economic-calendar upstream failed:", err);
        const stale = cache ?? dbEntry ?? { events: [], fetchedAt: now };
        cache = { ...stale, fetchedAt: now };
        return stale;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export const getGoldEconomicCalendar = createServerFn({ method: "GET" }).handler(
  async (): Promise<CacheEntry> => deliver(await loadEntry()),
);

/**
 * Server-only view for the actuals enrichment job: keeps releases up to 6h
 * past (late prints such as FOMC) and preserves original feed titles (no CPI
 * display rename), so official adapters and stored actuals match by title.
 */
export const getCalendarForEnrichment = createServerFn({ method: "GET" }).handler(
  async (): Promise<CacheEntry> => mergeActuals(finalize(await loadEntry(), 6 * 60 * 60 * 1000)),
);
